import { ChatLines as ChatLinesIcon, EditPencil as EditPencilIcon, PageEdit as PageEditIcon } from 'iconoir-react'
import { startCase } from 'lodash'
import { ReactElement, useMemo } from 'react'
import { LazyLocationCell } from '../LazyLocationCell'
import { LazyWorkspaceCell } from '../LazyWorkspaceCell'
import Spinner from '../Spinner'
import { ActivityCard } from './ActivityCard'
import { ActivityTags } from './ActivityTags'
import { KeyValueDrawer } from './KeyValueDrawer'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useImmediateRequest } from 'app/hooks/useImmediateRequest'
import { useShowUtmFields } from 'app/hooks/useShowUtmFields'
import type { ActivityJson } from 'app/types/entities'
import type { ImportJson } from 'app/types/entities'
import { useWorkspace } from 'app/utils'
import { assertBoom } from 'lib/assertBoom'

type Props = {
  activity: ActivityJson
  alwaysOpen?: boolean
  isFirst?: boolean
  isLast?: boolean
  /** See `ActivityCard`. */
  hideHeader?: boolean
  singleActivity?: boolean
}
/**
 * Import Card that renders Chat, Imported Form, Online Scheduler, or Virtual Assistant.
 * All import activities share same structure. They diferenciate by title and icon.
 * @Prop activity - Activity to query from.
 * @Prop alwaysOpen. Boolean. If true it hides drawer button and shows the content.
 * @Prop isFirst. Boolean. If true it hides the first activity union line.
 * @Prop isLast. Boolean. If true it hides the last activity union line.
 * @Prop singleActivity. Boolean. If true it hides the last activity union line.
 * @returns ReactElement
 */
export function ImportCard({ activity, alwaysOpen, isFirst, isLast, singleActivity, hideHeader }: Props): ReactElement {
  const workspace = useWorkspace()
  const showUtmFields = useShowUtmFields()
  assertBoom([`Chat`, `Imported Form`, `Online Scheduler`, `Virtual Consultation`].includes(activity.type!))

  // Some imported activities (legacy Chats) have no linked record; an unguarded
  // fetch would hit /Chat/null, 404, and raise the global error toast.
  const hasRecordId = Boolean(activity.typeId)
  const [response, responseError] = useImmediateRequest<{ record: ImportJson }>(
    hasRecordId ? `/api/activities/${activity.id}/${activity.type}/${activity.typeId}` : ``,
  )
  // responseError is an Error instance; lodash isEmpty() would call it empty
  // and leave the card spinning forever.
  const isLoading = hasRecordId && response === undefined && responseError === undefined
  let importActivity = useMemo(() => response?.record, [response])
  let icon = useMemo(() => {
    if (activity.type === `Chat`) {
      return <ChatLinesIcon className="h-5 w-5 rounded-md bg-green-600  p-1 text-on-color" />
    } else if (activity.type === `Imported Form`) {
      return <PageEditIcon className="h-5 w-5 rounded-md bg-inverse p-1 text-inverse" />
    }
    return <EditPencilIcon className="h-5 w-5 rounded-md bg-green-600 p-1 text-on-color" />
  }, [activity])
  let title = useMemo(() => {
    switch (activity.type) {
      case `Chat`:
        return `Imported Chat`
      case `Imported Form`:
        return `Imported Form`
      case `Online Scheduler`:
        return `Online Scheduler`
      case `Virtual Consultation`:
        return `Note`
      default:
        return ``
    }
  }, [activity])

  return (
    <ActivityCard
      hideHeader={hideHeader}
      title={<span className="text-ui-sm text-strong">{title}</span>}
      activityDate={activity?.activityDate}
      icon={icon}
      tags={<ActivityTags activityTags={activity?.tags} workspaceId={workspace?.id} isLoading={isLoading} />}
      preview={
        <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <KeyValueDrawer
            map={{
              Name: startCase(importActivity?.name) || `-`,
              'Phone Number': formatPhoneNumber(importActivity?.phone) || `-`,
              Email: activity.email || `-`,
            }}
          />
        </div>
      }
      drawer={
        <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          {isLoading && <Spinner size={4} />}
          {importActivity && (
            <KeyValueDrawer
              map={{
                Name: startCase(importActivity?.name) || `-`,
                'Phone Number': formatPhoneNumber(importActivity?.phone) || `-`,
                Email: activity.email || importActivity.email || `-`,
                'Requested Appointment Date': formatDate({ value: importActivity.requestedAppointmentTime }) || `-`,
                'Requested Treatment': importActivity.treatment || `-`,
                'Scheduled?': importActivity.isScheduled ? `Yes` : `No`,
                Channel: importActivity?.channel,
                ...(showUtmFields && { Source: importActivity.utmSource || `-` }),
                ...(showUtmFields && { Medium: importActivity.utmMedium || `-` }),
                ...(showUtmFields && { Campaign: importActivity.utmCampaign || `-` }),
                Staff: importActivity.staff || `-`,
                Vendor: importActivity.vendor,
                Workspace: (
                  <span className="text-ui-xs">
                    <LazyWorkspaceCell workspaceId={importActivity.workspaceId} />
                  </span>
                ),
                Location: (
                  <span className="text-ui-xs">
                    <LazyLocationCell locationId={importActivity.locationId!} />
                  </span>
                ),
              }}
              omitLabels={[`Staff`]}
            />
          )}
          {!isLoading && !importActivity && (
            <div className="w-full py-1 text-center text-body-xs text-subtle">
              Details are no longer available for this activity.
            </div>
          )}
        </div>
      }
      alwaysOpen={alwaysOpen}
      isFirst={isFirst}
      isLast={isLast}
      singleActivity={singleActivity}
    />
  )
}
